<!--
  Row 1 of the paginated trial balance: title and refresh on the left.
  On the right: closing balance difference, ERP sync, bulk upload, then the more menu.
  Activity log and export live in the menu. The log dialog stays the shared ActivityLog component.
-->
<template>
  <div
    class="trial-balance-paginated-header"
    data-testid="financialclose-project-trialbalance-paginated__headerbar"
  >
    <div class="trial-balance-paginated-header__title">
      <h3
        class="trial-balance-paginated-header__heading noTransform"
        data-testid="financialclose-project-trialbalance-paginated__title"
      >
        Trial Balance
      </h3>
      <v-tooltip bottom content-class="tooltip-bottom">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            height="24"
            width="24"
            icon
            class="shadow-off"
            :disabled="loading"
            v-on="on"
            data-testid="financialclose-project-trialbalance-paginated__refresh-btn"
            @click="$emit('refresh')"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </div>

    <div class="trial-balance-paginated-header__actions">
      <div class="trial-balance-paginated-header__action-row">
        <div
          v-if="showClosingDifference"
          class="trial-balance-paginated-header__closing-diff"
          data-testid="financialclose-project-trialbalance-paginated__closingbalancebar-diff"
        >
          <span class="trial-balance-paginated-header__closing-diff-label">Closing Balance Difference :</span>
          <span
            class="trial-balance-paginated-header__closing-diff-value"
            data-testid="financialclose-project-trialbalance-paginated__closingbalancebar-diff-value"
          >
            {{ $nova.formatNumber(closingBalanceTotal) }}
          </span>
        </div>
        <v-btn
          v-if="erpEnabled"
          class="secondaryBtn px-4 py-1"
          :disabled="loading || uploadInProgress"
          data-testid="financialclose-project-trialbalance-paginated__sync-btn"
          @click="$emit('sync')"
        >
          <v-icon class="pr-2">mdi-file-sync-outline</v-icon>
          <p class="ma-0 pa-0">Update from ERP</p>
        </v-btn>
        <v-tooltip
          v-if="isManager"
          bottom
          content-class="tooltip-bottom"
          :disabled="!uploadInProgress"
        >
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                color="primary"
                class="white--text radius-mini"
                :disabled="uploadInProgress"
                :loading="uploadInProgress"
                data-testid="financialclose-project-trialbalance-paginated__bulkupload-btn"
                @click="$emit('bulk-upload')"
              >
                <template v-slot:loader>
                  <v-progress-circular indeterminate size="20" width="2" color="white" />
                </template>
                <v-icon class="mr-1">mdi-upload-multiple</v-icon>
                <p>Bulk Upload</p>
              </v-btn>
            </span>
          </template>
          <span>{{ $options.TB_UPLOAD_MESSAGES.BUTTON_IN_PROGRESS }}</span>
        </v-tooltip>
        <v-menu
          offset-y
          transition="slide-y-transition"
          :close-on-content-click="true"
          content-class="trial-balance-paginated-header__more-menu-panel"
          data-testid="financialclose-project-trialbalance-paginated__more-menu"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="shadow-off"
              aria-label="More actions"
              data-testid="financialclose-project-trialbalance-paginated__more-menu-btn"
            >
              <img
                src="/img/icons/threedot.svg"
                alt="More actions"
                class="trial-balance-paginated-header__more-menu-img rotate-90"
              />
            </v-btn>
          </template>
          <div
            class="trial-balance-paginated-header__more-menu"
            data-testid="financialclose-project-trialbalance-paginated__more-menu-list"
          >
            <div class="trial-balance-paginated-header__more-menu-body">
              <button
                type="button"
                class="trial-balance-paginated-header__more-menu-item"
                data-testid="financialclose-project-trialbalance-paginated__more-menu-activity"
                @click.stop="openActivityLog"
              >
                <span class="trial-balance-paginated-header__more-menu-item-content">
                  <img src="/img/icons/activity-log.svg" alt="" class="trial-balance-paginated-header__more-menu-img" />
                  <span class="trial-balance-paginated-header__more-menu-label">Activity Log</span>
                </span>
              </button>
              <v-tooltip bottom content-class="tooltip-bottom" :disabled="!exportTooltip">
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="trial-balance-paginated-header__more-menu-item-wrap"
                    v-bind="attrs"
                    v-on="on"
                    :title="exportTooltip || null"
                  >
                    <button
                      type="button"
                      class="trial-balance-paginated-header__more-menu-item"
                      :disabled="exportDisabled"
                      :title="exportTooltip || null"
                      data-testid="financialclose-project-trialbalance-paginated__more-menu-export"
                      @click.stop="onExport"
                    >
                      <span class="trial-balance-paginated-header__more-menu-item-content">
                        <v-progress-circular
                          v-if="exporting"
                          indeterminate
                          size="16"
                          width="2"
                          color="grey"
                        />
                        <img
                          v-else
                          src="/img/icons/fbc-icons/ExportIcon.svg"
                          alt=""
                          class="trial-balance-paginated-header__more-menu-img"
                        />
                        <span class="trial-balance-paginated-header__more-menu-label">{{ exporting ? 'Exporting' : 'Export' }}</span>
                      </span>
                    </button>
                  </span>
                </template>
                <span>{{ exportTooltip }}</span>
              </v-tooltip>
            </div>
          </div>
        </v-menu>
      </div>
      <p
        v-if="lastUpdated"
        class="trial-balance-paginated-header__lastsync"
        data-testid="financialclose-project-trialbalance-paginated__lastsync"
      >
        Last sync:
        {{ $nova.formatDateTime(new Date(lastUpdated).getTime()) }}
      </p>
      <activity-log
        v-if="metadataId"
        hide-trigger
        ref="activityLog"
        :resource-id="metadataId"
        row-description="Trial Balance Actions"
        :type="activityLogType"
        data-testid="financialclose-project-trialbalance-paginated__action-activitylog-btn"
      />
    </div>
  </div>
</template>

<script>
import './TrialBalancePaginatedHeader.scss';
import { ActivityLog } from '../../../../components';
import { TB_UPLOAD_MESSAGES } from '../../../../utils/constants';

export default {
  name: 'TrialBalancePaginatedHeader',

  TB_UPLOAD_MESSAGES,

  components: { ActivityLog },

  props: {
    loading: { type: Boolean, default: false },
    uploadInProgress: { type: Boolean, default: false },
    erpEnabled: { type: Boolean, default: false },
    isManager: { type: Boolean, default: false },
    lastUpdated: { type: [String, Number], default: '' },
    exporting: { type: Boolean, default: false },
    exportDisabled: { type: Boolean, default: false },
    exportTooltip: { type: String, default: '' },
    showClosingDifference: { type: Boolean, default: false },
    closingBalanceTotal: { type: Number, default: 0 },
    metadataId: { type: [String, Number, Object], default: '' },
    activityLogType: { type: [Array, String], default: () => [] },
  },

  methods: {
    openActivityLog() {
      const activityLog = this.$refs.activityLog;
      if (activityLog && typeof activityLog.openDialog === 'function') {
        activityLog.openDialog();
      }
    },
    onExport() {
      if (this.exportDisabled) return;
      this.$emit('export');
    },
  },
};
</script>
